import React from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ServicePageLayout } from '../components/ServicePageLayout';
export function ExteriorDetailingPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Exterior Car Detailing',
    provider: {
      '@type': 'LocalBusiness',
      name: "Mike's Pro Detail",
      telephone: '+18183819905'
    },
    areaServed: ['Ventura County', 'Camarillo', 'Ventura', 'Thousand Oaks'],
    description: 'Professional mobile exterior car detailing services including hand wash, clay bar treatment, wheel cleaning, and wax protection.'
  };
  const faqs = [{
    question: 'What is included in exterior detailing?',
    answer: 'Our exterior detail includes a safe hand wash, deep wheel and tire cleaning, clay bar decontamination, bug and tar removal, premium wax or sealant application, and tire and trim dressing.'
  }, {
    question: 'Will exterior detailing remove swirl marks?',
    answer: 'A standard exterior detail cleans and protects but does not remove swirl marks. For added gloss and water beading, pair it with our hybrid ceramic spray protection.'
  }, {
    question: 'How long does exterior protection last?',
    answer: 'Our hybrid ceramic wax and polymer sealants typically provide several months of protection and water beading, depending on conditions and how often the vehicle is washed.'
  }, {
    question: 'How much does exterior detailing cost?',
    answer: 'Exterior detailing starts at $160, with pricing adjusted for vehicle size and condition. Call (818) 381-9905 for an exact quote.'
  }, {
    question: 'Do you bring your own water?',
    answer: 'Yes. We bring our own spot-free water and detailing equipment. We require access to a standard electrical outlet at the service location.'
  }];
  const breadcrumbs = [{
    name: 'Home',
    item: 'https://www.fulldetailpro.com/'
  }, {
    name: 'Services',
    item: 'https://www.fulldetailpro.com/services'
  }, {
    name: 'Exterior Detailing',
    item: 'https://www.fulldetailpro.com/services/exterior-detailing'
  }];
  return <ServicePageLayout title="Exterior Detailing Ventura County" subtitle="Protect your paint and restore a brilliant shine with our premium mobile exterior detailing services. We bring the ultimate wash and wax directly to you." metaDescription="Top-rated mobile exterior detailing in Ventura County \u2014 Camarillo, Ventura & Thousand Oaks. Hand wash, clay bar & ceramic wax protection. Call (818) 381-9905." schema={schema} faqs={faqs} breadcrumbs={breadcrumbs} data-id="element-717" data-mp="28c9a7rqdvei0">
      <p data-id="element-718" data-mp="1kldcyf35rr3d">
        Your vehicle's exterior faces a constant barrage of environmental
        hazards: UV rays, bird droppings, tree sap, road tar, and industrial
        fallout. Over time, these contaminants degrade your clear coat, leaving
        your paint looking dull and feeling rough. At Mike's Pro Detail, our{' '}
        <strong data-id="element-719" data-mp="3azvyap97v5an">mobile exterior car detailing</strong> service is designed to
        safely remove these contaminants, restore a deep gloss, and apply
        durable protection. Whether you need car detailing in Camarillo,
        Thousand Oaks, or anywhere in Ventura County, our mobile unit provides a
        superior alternative to automated car washes that scratch and swirl your
        paint.
      </p>

      <h2 data-id="element-720" data-mp="1ahf2b7ubjyjm">Our Exterior Detailing Process</h2>
      <p data-id="element-721" data-mp="ds7fzxlvllen">
        We utilize safe, scratch-free wash methods and premium products to
        ensure your vehicle looks its absolute best. Our exterior detailing
        package includes:
      </p>

      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-722" data-mp="msvs2mrplbow">
        <li className="flex items-start" data-id="element-723" data-mp="mraz31l1m0t5">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-724" data-mp="rrgwyzhwojjo" />
          <span data-id="element-725" data-mp="rpm9pej5wfcf">
            A meticulous hand wash using premium microfiber towels and
            pH-neutral soap to safely lift dirt without inducing swirl marks.
          </span>
        </li>
        <li className="flex items-start" data-id="element-726" data-mp="238ys5alf8aul">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-727" data-mp="21p6vf352aifz" />
          <span data-id="element-728" data-mp="21rjj7wxi3n0y">
            <strong data-id="element-729" data-mp="1wqvl6rauu5x8">Deep Wheel & Tire Cleaning:</strong> We use specialized
            brushes and non-acidic cleaners to remove baked-on brake dust from
            wheel barrels, faces, and tires.
          </span>
        </li>
        <li className="flex items-start" data-id="element-730" data-mp="26nqgz2fqts7t">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-731" data-mp="26qw5afgjsq2h" />
          <span data-id="element-732" data-mp="2kmzxy4brwqcu">
            <strong data-id="element-733" data-mp="2kmht4jb1p3tb">Clay Bar Treatment:</strong> A crucial step that removes
            embedded contaminants (like overspray and rail dust) that washing
            alone cannot remove, leaving the paint smooth as glass.
          </span>
        </li>
        <li className="flex items-start" data-id="element-734" data-mp="2pf15xe1wrux2">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-735" data-mp="2pg2sofsae5um" />
          <span data-id="element-736" data-mp="33g3txwcgrfam">
            <strong data-id="element-737" data-mp="33flnk6w37koz">Bug & Tar Removal:</strong> Safe breakdown and removal of
            stubborn bug splatter and road tar from the front bumper and lower
            rocker panels.
          </span>
        </li>
        <li className="flex items-start" data-id="element-738" data-mp="3i2ki4aq8qv7x">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-739" data-mp="3n1duu0xpei71" />
          <span data-id="element-740" data-mp="3myhu64uh1few">
            <strong data-id="element-741" data-mp="391cseh1sj2pg">Premium Wax / Sealant Application:</strong> Application of a
            high-quality hybrid ceramic wax or polymer sealant to provide months
            of protection and intense water beading.
          </span>
        </li>
        <li className="flex items-start" data-id="element-742" data-mp="2zahwekyqsoqt">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-743" data-mp="341zvf3h7gf95" />
          <span data-id="element-744" data-mp="343ay0zywzgyo">
            <strong data-id="element-745" data-mp="343t4epfajbkb">Tire & Trim Dressing:</strong> Application of a no-sling,
            UV-blocking dressing to tires and faded exterior plastics to restore
            a rich, dark appearance.
          </span>
        </li>
      </ul>

      <h2 data-id="element-746" data-mp="2gbvuj3veitlu">Why Exterior Detailing is Essential</h2>
      <p data-id="element-747" data-mp="2gd75bnn9l1mg">
        Regular exterior detailing is preventative maintenance for your
        vehicle's paint. The Southern California sun can quickly cause clear
        coat failure and oxidation if the paint is left unprotected. By
        regularly removing bonded contaminants through a clay bar treatment and
        applying a protective wax or sealant, you create a sacrificial barrier
        against the elements. This not only keeps your car looking stunning but
        also preserves its resale value and prevents costly paint repairs down
        the line.
      </p>

      <h2 data-id="element-748" data-mp="19suscd5xs1p6">The Premier Choice for Mobile Auto Detailing Camarillo CA</h2>
      <p data-id="element-749" data-mp="14ujdfo0sb103">
        When you search for "Mobile Detailing Ventura County" or need reliable
        service in Thousand Oaks, Mike's Pro Detail is the trusted choice. We
        bring our own spot-free water and detailing equipment, and we require
        access to a standard electrical outlet at the service location, meaning
        we can perform a flawless exterior detail in your driveway, at your
        office parking lot, or even at your apartment complex. We treat every vehicle as if it were
        our own, ensuring that every inch of your exterior receives the
        attention it deserves.
      </p>

      <h2 data-id="element-750" data-mp="212pgj6wov4x4">Related Detailing Services</h2>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-751" data-mp="20zjuk0jcwj5m">
        <Link to="/services/hybrid-ceramic-spray" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-752" data-mp="251om8tq8v5pm">
          Hybrid Ceramic Spray &rarr;
        </Link>
        <Link to="/full-detail" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-753" data-mp="2jrma18k9htea">
          Full Car Detail &rarr;
        </Link>
        <Link to="/services/mobile-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-754" data-mp="2ssailnqvz1js">
          Mobile Detailing &rarr;
        </Link>
      </div>
    </ServicePageLayout>;
}